<template>
  <model-form
    ref="form"
    :model="seatChart"
    @revert="revert()"
  >
    <template #header-controls>
      <support-menu
        video="Building Seat Chart"
      />
    </template>
    <div class="row q-col-gutter-md items-start">
      <div class="col-12 col-sm-6">
        <div class="text-h5 q-mb-sm">
          Preview
        </div>

        <seat-chart
          :edit-mode="true"
          :seat-chart="demoSeatChart"
          :seats-to-select="0"
          :editing-seats="selectedSeats"
          :show-seat-numbers="showSeatNumbers"
          @select-seat="handleSeatSelection($event)"
          @drag-selection="handleDragSelection($event)"
        />
        <div class="row">
          <q-checkbox
            v-model="showSeatNumbers"
            label="Show Seat Numbers"
          />
          <q-space />
          <q-checkbox
            v-if="config.socialDistancing"
            v-model="demoSocialDistancing"
            label="Demo Social Distancing"
          />
        </div>
      </div>
      <div class="col-12 col-sm-6">
        <div class="text-h5 q-mb-sm">
          Controls
        </div>

        <div class="row flex-wrap q-col-gutter-md">
          <q-input
            v-model="seatChart.name"
            class="col-6"
            label="Name"
            bottom-slots
            :rules="[val => !!val || 'Field is required']"
          />
          <q-select
            v-model="config.primarySeatType"
            class="col-6"
            label="Primary Seat Type"
            :options="$mh.asOptions(SEAT_TYPES.OPTIONS)"
            map-options
            emit-value
          >
            <template #option="scope">
              <q-item
                v-bind="scope.itemProps"
                v-on="scope.itemEvents"
              >
                <q-item-section>
                  <div class="row items-center justify-between q-gutter-x-sm">
                    <span>{{ scope.opt.label }}</span>
                    <div
                      style="max-width: 25px;"
                      class="seat col-auto"
                    >
                      <div
                        class="seat-content"
                      >
                        <svg viewBox="0 0 220 220">
                          <use :xlink:href="'/seats.svg#' + scope.opt.value" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </q-item-section>
              </q-item>
            </template>
          </q-select>
          <div class="col-6">
            <q-item-label>Column Count</q-item-label>
            <q-slider
              v-model.number="config.columnCount"
              label
              type="number"
              :min="1"
              :max="50"
            />
          </div>
          <div class="col-6">
            <q-item-label>Row Count</q-item-label>
            <q-slider
              v-model.number="config.rowCount"
              class="col-4"
              label
              type="number"
              :min="1"
              :max="32"
            />
          </div>
          <q-checkbox
            v-model.number="config.descendingRows"
            label="Descending Rows"
            class="col-6"
          />
          <q-checkbox
            v-model.number="config.reverseColumns"
            label="Reverse Columns"
            class="col-6"
          />
          <q-select
            v-model="config.columnAisles"
            class="col-6"
            label="Column Aisles"
            multiple
            use-chips
            map-options
            emit-value
            :options="colAisleOptions"
          />
          <q-select
            v-model="config.rowAisles"
            class="col-6"
            label="Row Aisles"
            multiple
            use-chips
            map-options
            emit-value
            :options="rowAisleOptions"
          />
          <q-checkbox
            v-model.number="config.allowGaps"
            label="Allow Seat Gaps"
            class="col-6"
          >
            <q-tooltip>
              <div style="max-width: 400px;">
                <div style="font-size: 16px;">
                  Seat gap is when seats in a row are selected with an empty seat them. By default this is not allowed.
                </div>
                <img
                  class="full-width"
                  src="/images/admin/Seat Gap.jpg"
                >
              </div>
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model.number="config.allowSeatOrphans"
            label="Allow Seat Orphans"
            class="col-6"
          >
            <q-tooltip>
              <div style="max-width: 400px;">
                <div style="font-size: 16px;">
                  An orphan seat is when a single seat is left available and by selecting left or right one seat could prevent this. By default this selection type is not allowed.
                </div>
                <img
                  class="full-width"
                  src="/images/admin/Orphan Seat.jpg"
                >
              </div>
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model.number="config.allowPartialCouchSale"
            label="Allow Partial Couch Sale"
            class="col-6"
          >
            <q-tooltip>
              <div style="max-width: 400px;">
                <div style="font-size: 16px;">
                  Partial Couch Sale is when a two seat couch has only one seat selected. By default this is not allowed.
                </div>
                <img
                  class="full-width"
                  src="/images/admin/Partial Couch Sale.jpg"
                >
              </div>
            </q-tooltip>
          </q-checkbox>
          <div class="row q-col-gutter-md">
            <q-toggle
              v-model="config.socialDistancing"
              class="col-12"
              label="Social Distancing"
            />
            <div
              v-if="config.socialDistancing"
              class="col-4"
            >
              <q-item-label>Horizontal Spacing</q-item-label>
              <q-slider
                v-model.number="config.columnGap"
                type="number"
                label
                :min="0"
                :max="3"
              />
            </div>
            <div
              v-if="config.socialDistancing"
              class="col-4"
            >
              <q-item-label>Front-Back Spacing</q-item-label>
              <q-slider
                v-model.number="config.rowGap"
                class="col-4"
                type="number"
                label
                :min="0"
                :max="3"
              />
            </div>
            <div
              v-if="config.socialDistancing"
              class="col-4"
            >
              <q-checkbox
                v-model.number="config.allowCorners"
                label="Allow Corners"
              />
            </div>
          </div>
        </div>
        <div class="col-12 column">
          <div class="text-h5 q-mb-sm q-mt-lg row items-center">
            <span>Overrides ({{ overrides.length }})</span>
            <div class="q-ml-sm">
              <q-btn
                class="self-center"
                round
                icon="add"
                @click="addOverride()"
              />
              <q-btn
                v-if="selectedOverride && !hasAnyOverridesSet(selectedOverride)"
                class="text-negative"
                round
                icon="delete_outline"
                @click="deleteOverride(editingId)"
              />
              <q-btn
                v-if="selectedOverride"
                round
                icon="close"
                @click="editingId = null"
              />
            </div>
          </div>
          <div class="row q-gutter-sm items-start">
            <override
              v-for="override in selectedOverride ? [selectedOverride] : overrides"
              :key="override.id"
              :override="override"
              :row-count="+config.rowCount"
              :column-count="+config.columnCount"
              :editing-id="editingId"
              @edit="editingId = $event"
              @delete-override="deleteOverride($event)"
            />
          </div>
        </div>
      </div>
    </div>
  </model-form>
</template>

<script>

import { AVAILABILITY_TYPES, overrideInitialValues, SEAT_TYPES } from 'src/models/screen/SeatChart'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import Override from 'src/modules/admin/seat-chart/Override'
import SeatChart from 'src/modules/admin/seat-chart/SeatChart'
import SupportMenu from 'src/modules/admin/support/SupportMenu'

const charMap = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z',
  'AA', 'AB', 'AC', 'AD', 'AE', 'AF', 'AG', 'AH', 'AI', 'AJ', 'AK', 'AL', 'AM', 'AN', 'AO', 'AP', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AV', 'AW', 'AX', 'AY', 'AZ',
  'BA', 'BB', 'BC', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BK', 'BL', 'BM', 'BN', 'BO', 'BP', 'BQ', 'BR', 'BS', 'BT', 'BU', 'BV', 'BW', 'BX', 'BY', 'BZ',
  'CA', 'CB', 'CC', 'CD', 'CE', 'CF', 'CG', 'CH', 'CI', 'CJ', 'CK', 'CL', 'CM', 'CN', 'CO', 'CP', 'CQ', 'CR', 'CS', 'CT', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ',
  'DA', 'DB', 'DC', 'DD', 'DE', 'DF', 'DG', 'DH', 'DI', 'DJ', 'DK', 'DL', 'DM', 'DN', 'DO', 'DP', 'DQ', 'DR', 'DS', 'DT', 'DU', 'DV', 'DW', 'DX', 'DY', 'DZ',
  'EA', 'EB', 'EC', 'ED', 'EE', 'EF', 'EG', 'EH', 'EI', 'EJ', 'EK', 'EL', 'EM', 'EN', 'EO', 'EP', 'EQ', 'ER', 'ES', 'ET', 'EU', 'EV', 'EW', 'EX', 'EY', 'EZ',
  'FA', 'FB', 'FC', 'FD', 'FE', 'FF', 'FG', 'FH', 'FI', 'FJ', 'FK', 'FL', 'FM', 'FN', 'FO', 'FP', 'FQ', 'FR', 'FS', 'FT', 'FU', 'FV', 'FW', 'FX', 'FY', 'FZ',
  'GA', 'GB', 'GC', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GJ', 'GK', 'GL', 'GM', 'GN', 'GO', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GV', 'GW', 'GX', 'GY', 'GZ',
  'HA', 'HB', 'HC', 'HD', 'HE', 'HF', 'HG', 'HH', 'HI', 'HJ', 'HK', 'HL', 'HM', 'HN', 'HO', 'HP', 'HQ', 'HR', 'HS', 'HT', 'HU', 'HV', 'HW', 'HX', 'HY', 'HZ',
  'IA', 'IB', 'IC', 'ID', 'IE', 'IF', 'IG', 'IH', 'II', 'IJ', 'IK', 'IL', 'IM', 'IN', 'IO', 'IP', 'IQ', 'IR', 'IS', 'IT', 'IU', 'IV', 'IW', 'IX', 'IY', 'IZ',
  'JA', 'JB', 'JC', 'JD', 'JE', 'JF', 'JG', 'JH', 'JI', 'JJ', 'JK', 'JL', 'JM', 'JN', 'JO', 'JP', 'JQ', 'JR', 'JS', 'JT', 'JU', 'JV', 'JW', 'JX', 'JY', 'JZ',
  'KA', 'KB', 'KC', 'KD', 'KE', 'KF', 'KG', 'KH', 'KI', 'KJ', 'KK', 'KL', 'KM', 'KN', 'KO', 'KP', 'KQ', 'KR', 'KS', 'KT', 'KU', 'KV', 'KW', 'KX', 'KY', 'KZ',
  'LA', 'LB', 'LC', 'LD', 'LE', 'LF', 'LG', 'LH', 'LI', 'LJ', 'LK', 'LL', 'LM', 'LN', 'LO', 'LP', 'LQ', 'LR', 'LS', 'LT', 'LU', 'LV', 'LW', 'LX', 'LY', 'LZ',
  'MA', 'MB', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MI', 'MJ', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ',
  'NA', 'NB', 'NC', 'ND', 'NE', 'NF', 'NG', 'NH', 'NI', 'NJ', 'NK', 'NL', 'NM', 'NN', 'NO', 'NP', 'NQ', 'NR', 'NS', 'NT', 'NU', 'NV', 'NW', 'NX', 'NY', 'NZ',
  'OA', 'OB', 'OC', 'OD', 'OE', 'OF', 'OG', 'OH', 'OI', 'OJ', 'OK', 'OL', 'OM', 'ON', 'OO', 'OP', 'OQ', 'OR', 'OS', 'OT', 'OU', 'OV', 'OW', 'OX', 'OY', 'OZ',
  'PA', 'PB', 'PC', 'PD', 'PE', 'PF', 'PG', 'PH', 'PI', 'PJ', 'PK', 'PL', 'PM', 'PN', 'PO', 'PP', 'PQ', 'PR', 'PS', 'PT', 'PU', 'PV', 'PW', 'PX', 'PY', 'PZ',
  'QA', 'QB', 'QC', 'QD', 'QE', 'QF', 'QG', 'QH', 'QI', 'QJ', 'QK', 'QL', 'QM', 'QN', 'QO', 'QP', 'QQ', 'QR', 'QS', 'QT', 'QU', 'QV', 'QW', 'QX', 'QY', 'QZ',
  'RA', 'RB', 'RC', 'RD', 'RE', 'RF', 'RG', 'RH', 'RI', 'RJ', 'RK', 'RL', 'RM', 'RN', 'RO', 'RP', 'RQ', 'RR', 'RS', 'RT', 'RU', 'RV', 'RW', 'RX', 'RY', 'RZ',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SF', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SP', 'SQ', 'SR', 'SS', 'ST', 'SU', 'SV', 'SW', 'SX', 'SY', 'SZ',
  'TA', 'TB', 'TC', 'TD', 'TE', 'TF', 'TG', 'TH', 'TI', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TP', 'TQ', 'TR', 'TS', 'TT', 'TU', 'TV', 'TW', 'TX', 'TY', 'TZ',
  'UA', 'UB', 'UC', 'UD', 'UE', 'UF', 'UG', 'UH', 'UI', 'UJ', 'UK', 'UL', 'UM', 'UN', 'UO', 'UP', 'UQ', 'UR', 'US', 'UT', 'UU', 'UV', 'UW', 'UX', 'UY', 'UZ',
  'VA', 'VB', 'VC', 'VD', 'VE', 'VF', 'VG', 'VH', 'VI', 'VJ', 'VK', 'VL', 'VM', 'VN', 'VO', 'VP', 'VQ', 'VR', 'VS', 'VT', 'VU', 'VV', 'VW', 'VX', 'VY', 'VZ',
  'WA', 'WB', 'WC', 'WD', 'WE', 'WF', 'WG', 'WH', 'WI', 'WJ', 'WK', 'WL', 'WM', 'WN', 'WO', 'WP', 'WQ', 'WR', 'WS', 'WT', 'WU', 'WV', 'WW', 'WX', 'WY', 'WZ',
  'XA', 'XB', 'XC', 'XD', 'XE', 'XF', 'XG', 'XH', 'XI', 'XJ', 'XK', 'XL', 'XM', 'XN', 'XO', 'XP', 'XQ', 'XR', 'XS', 'XT', 'XU', 'XV', 'XW', 'XX', 'XY', 'XZ',
  'YA', 'YB', 'YC', 'YD', 'YE', 'YF', 'YG', 'YH', 'YI', 'YJ', 'YK', 'YL', 'YM', 'YN', 'YO', 'YP', 'YQ', 'YR', 'YS', 'YT', 'YU', 'YV', 'YW', 'YX', 'YY', 'YZ',
  'ZA', 'ZB', 'ZC', 'ZD', 'ZE', 'ZF', 'ZG', 'ZH', 'ZI', 'ZJ', 'ZK', 'ZL', 'ZM', 'ZN', 'ZO', 'ZP', 'ZQ', 'ZR', 'ZS', 'ZT', 'ZU', 'ZV', 'ZW', 'ZX', 'ZY', 'ZZ'
]

export default {
  name: 'EditSeatChartForm',
  components: {
    ModelForm,
    Override,
    SupportMenu,
    SeatChart
  },
  data () {
    return {
      editingId: null,
      showSeatNumbers: true,
      demoSocialDistancing: true,
      defaultConfig: {
        rowCount: 0,
        columnCount: 1,
        primarySeatType: SEAT_TYPES.REGULAR_SEAT,
        rowAisles: [],
        columnAisles: [],
        socialDistancing: false,
        columnGap: 2,
        rowGap: 1,
        cornerGaps: false,
        allowGaps: false,
        overrides: []
      },
      initializing: true
    }
  },
  computed: {
    config () { return this.seatChart?.seatChartConfiguration },
    overrides () { return this.config?.overrides || [] },
    nextOverrideId () { return +(_.max(_.map(this.overrides, 'id')) || 0) + 1 },
    seatChartId () { return this.$route.params.id },
    rowAisleOptions () {
      return _.times(this.config.rowCount - 1, (row) => {
        return {
          label: `After ${charMap[row]}`,
          value: charMap[row]
        }
      })
    },
    colAisleOptions () {
      return _.times(this.config.columnCount - 1, (column) => {
        return {
          label: `After ${column + 1}`,
          value: column + 1
        }
      })
    },
    selectedOverride () {
      return this.overrides.find((override) => { return override.id === this.editingId })
    },
    selectedSeats () {
      return this.seatsForOverride(this.selectedOverride)
    },
    computedSeatChart () {
      if (!this.config || !this.config.rowCount) {
        return []
      }

      let row = 0
      let column
      let previousWasRowAisle = false
      let previousWasColumnAisle = false
      let previousRowName
      let previousColumn

      const seatChart =
        _.map(_.times(+this.config.rowCount + this.config.rowAisles.length, () => {
          // Row loop
          if (!previousWasRowAisle) {
            row += 1
          }
          const rowName = charMap[row - 1]
          const rowAisle = !previousWasRowAisle && _.includes(this.config.rowAisles, previousRowName)
          previousRowName = rowName
          previousWasRowAisle = rowAisle

          column = 0
          return _.map(_.times(+this.config.columnCount + this.config.columnAisles.length, (_columnIndex) => {
            // Column/Seat loop
            if (!previousWasColumnAisle) {
              column += 1
            }
            const columnAisle = !previousWasColumnAisle && _.includes(this.config.columnAisles, previousColumn)
            previousWasColumnAisle = columnAisle
            previousColumn = column
            if (rowAisle || columnAisle) {
              return {
                seatType: 'aisle',
                available: false
              }
            }

            let available = true
            let availabilityType = AVAILABILITY_TYPES.AVAILABLE
            let availableStartTime = null
            let availableEndTime = null
            let seatType = this.config.primarySeatType
            let seatName

            if (this.config.descendingRows) {
              seatName = charMap[this.config.rowCount - row]
            } else {
              seatName = rowName
            }
            if (this.config.reverseColumns) {
              seatName += this.config.columnCount - column + 1
            } else {
              seatName += column
            }

            let seatId
            seatId = rowName
            seatId += column

            let reserveDuringMarathons = null
            let customSeatType = null
            let customSeatTypeName = ''
            let customSeatTypeColor = null
            let heldSeatSectionName = null
            let confirmationRequired = null
            let confirmationMessage = null
            let confirmationExpandInfo = null
            let areaCategoryId = null

            _.each(this.overrides, (override) => {
              if (override.rowStart === null && override.columnStart === null) return

              if (!_.isNil(override.rowStart) && (rowName < override.rowStart || rowName > (override.rowEnd || override.rowStart))) return
              if (!_.isNil(override.columnStart) && (column < override.columnStart || column > (override.columnEnd || override.columnStart))) return

              if (!_.isNil(override.seatType)) {
                seatType = override.seatType
              }

              if (seatType === 'aisle') {
                available = false
                availabilityType = AVAILABILITY_TYPES.AISLE
              } else if (!_.isNil(override.available)) {
                available = override.available

                if (available) {
                  availabilityType = AVAILABILITY_TYPES.AVAILABLE
                } else if (!_.isNil(override.availabilityType)) {
                  availabilityType = override.availabilityType
                } else if (_.isNil(availabilityType) || availabilityType === AVAILABILITY_TYPES.AVAILABLE) {
                  availabilityType = AVAILABILITY_TYPES.UNAVAILABLE
                }
              }

              if (!_.isNil(override.availableStartTime)) {
                availableStartTime = override.availableStartTime
              }

              if (!_.isNil(override.availableEndTime)) {
                availableEndTime = override.availableEndTime
              }

              if (!_.isNil(override.seatName) && override.seatName.length > 0) {
                seatName = override.seatName
              }

              if (!_.isNil(override.heldSeatSectionName) && override.heldSeatSectionName.length > 0) {
                heldSeatSectionName = override.heldSeatSectionName
              }

              if (!_.isNil(override.reserveDuringMarathons)) {
                reserveDuringMarathons = override.reserveDuringMarathons
              }

              if (!_.isNil(override.areaCategoryId)) {
                areaCategoryId = override.areaCategoryId
              }

              if (!_.isNil(override.customSeatType)) {
                customSeatType = override.customSeatType
                if (customSeatType) {
                  if (!_.isNil(override.customSeatTypeName) && override.customSeatTypeName.length > 0) customSeatTypeName = override.customSeatTypeName
                  if (!_.isNil(override.customSeatTypeColor)) customSeatTypeColor = override.customSeatTypeColor
                }
              }

              if (!_.isNil(override.confirmationRequired)) {
                confirmationRequired = override.confirmationRequired
                if (confirmationRequired) {
                  if (!_.isNil(override.confirmationMessage)) confirmationMessage = override.confirmationMessage
                  if (!_.isNil(override.confirmationExpandInfo)) confirmationExpandInfo = override.confirmationExpandInfo
                }
              }
            })

            return {
              id: seatId,
              name: seatName,
              seatType,
              available,
              availabilityType,
              availableStartTime,
              availableEndTime,
              reserveDuringMarathons,
              areaCategoryId,
              customSeatType,
              customSeatTypeName,
              customSeatTypeColor,
              heldSeatSectionName,
              confirmationRequired,
              confirmationMessage,
              confirmationExpandInfo
            }
          }))
        }))
      return seatChart
    },
    demoSeatChart () {
      const seatChart = this.seatChart.clone()
      seatChart.seatChart = this.computedSeatChart
      if (this.config.socialDistancing && this.demoSocialDistancing) {
        // Add two seats right in the center of the auditorium as a demo
        const column = Math.round(this.config.columnCount / 2)
        const row = Math.round(this.config.rowCount / 2)

        this.setupSocialDistancingDemo(seatChart.seatChart, column - 1, row)
        this.setupSocialDistancingDemo(seatChart.seatChart, column, row)
        this.setupSocialDistancingDemo(seatChart.seatChart, column + 1, row)
      }

      return seatChart
    },
    seatIdToNameMap () {
      return _(this.computedSeatChart).flatten().keyBy('id').mapValues('name').value()
    }
  },
  watch: {
    editingId (_currentId, previousId) {
      if (!previousId) return

      // If we finish editing and there is no modification, delete it
      const previousOverride = _.find(this.overrides, { id: previousId })
      if (!previousOverride) return

      if (!this.hasAnyOverridesSet(previousOverride)) {
        this.deleteOverride(previousId)
      }
    },
    async 'seatChart.loading' () {
      // If the load has started or completed, we are initializing.
      // This is to ensure that only user activity sets the seat chart.
      if (this.seatChart.loading) {
        this.initializing = true
      } else {
        await this.$nextTick()
        this.initializing = false
      }
    },
    computedSeatChart () {
      // This vue componenent is responsible for building the finalized seat chart (TODO: move this to the SeatChart.js model)

      // If we are initializing, don't set the seat chart.
      // The reason we need this is so new default values can be added without triggering a dirty state on the seat chart.
      if (this.initializing) return

      this.seatChart.seatChart = this.computedSeatChart
    }
  },
  created () {
    _.extend(this, { SEAT_TYPES, AVAILABILITY_TYPES })
  },
  methods: {
    setupSocialDistancingDemo (seatChart, seatColumn, seatRow) {
      if (!seatChart[seatRow] || !seatChart[seatRow][seatColumn]) return

      if (!seatChart[seatRow][seatColumn].available && !seatChart[seatRow][seatColumn]['social-distancing']) return

      seatChart[seatRow][seatColumn].available = false
      seatChart[seatRow][seatColumn]['social-distancing'] = false

      for (let rowGap = -this.config.rowGap; rowGap <= this.config.rowGap; rowGap++) {
        for (let columnGap = -this.config.columnGap; columnGap <= this.config.columnGap; columnGap++) {
          if (rowGap === 0 && columnGap === 0) continue

          const row = seatRow + rowGap
          const col = seatColumn + columnGap

          if (row < 0 || col < 0) continue

          if (this.config.allowCorners &&
            this.config.columnGap > 0 && this.config.rowGap > 0 && // If either of these is 0 then the concept of corners doesn't make sense
            Math.abs(columnGap) === this.config.columnGap && Math.abs(rowGap) === this.config.rowGap
          ) continue

          if (!seatChart[row]) continue

          const seat = seatChart[row][col]

          if (!seat) continue

          if (seat.available) {
            seat.available = false
            seat['social-distancing'] = true
          }
        }
      }
    },
    show (seatChart) {
      this.$refs.form.show(seatChart)
    },
    seatsForOverride (override) {
      if (!override) return []

      if (override.rowStart === null && override.columnStart === null) return []

      const seats = []
      for (let rowIndex = charMap.indexOf(override.rowStart || 'A'); rowIndex <= charMap.indexOf(override.rowEnd || override.rowStart || 'Z'); rowIndex++) {
        for (let columnIndex = (override.columnStart || 1); columnIndex <= +(override.columnEnd || override.columnStart || 100); columnIndex++) {
          const seatId = charMap[rowIndex] + columnIndex
          seats.push(seatId)
        }
      }
      return seats
    },
    addOverride () {
      const options = {
        rowStart: null,
        rowEnd: null,
        columnStart: null,
        columnEnd: null,
        id: this.nextOverrideId
      }

      this.overrides.push(_.extend(options, _.cloneDeep(overrideInitialValues)))

      this.editingId = options.id
    },
    getRowName (seat) {
      if (isNaN(seat[1])) {
        return seat.slice(0, 2)
      } else {
        return seat[0]
      }
    },
    getColumn (seat) {
      if (isNaN(seat[1])) {
        return +seat.slice(2)
      } else {
        return +seat.slice(1)
      }
    },
    isSeatInOverride (seat, override) {
      // See if we have a selection here
      const rowName = this.getRowName(seat)
      const column = this.getColumn(seat)

      if (override.rowStart === null && override.columnStart === null) return false

      if (override.rowStart !== null && (rowName < override.rowStart || rowName > (override.rowEnd || override.rowStart))) return false
      if (override.columnStart !== null && (column < override.columnStart || column > (override.columnEnd || override.columnStart))) return false

      return true
    },
    handleSeatSelection (seat) {
      const priorSelectionId = this.editingId
      this.editingId = null

      if (seat) {
        const overridesForSeat = _.filter(this.overrides, override => { return this.isSeatInOverride(seat, override) })

        const newSelectionContaintedInPriorSelection = _.some(overridesForSeat, { id: priorSelectionId })
        const cycleThroughSelections = newSelectionContaintedInPriorSelection

        const override = _.find(overridesForSeat, (override) => {
          // Allow use to cycle through multiple selections and eventually add an override
          if (cycleThroughSelections && priorSelectionId && override.id <= priorSelectionId) return false

          return this.isSeatInOverride(seat, override)
        })

        if (override) {
          this.editingId = override.id
          return
        }

        const options = {
          rowStart: this.getRowName(seat),
          rowEnd: null,
          columnStart: this.getColumn(seat),
          columnEnd: null
        }

        // Prevent creating duplicate overrides for identical selections
        if (_.some(this.overrides, options)) return

        options.id = this.nextOverrideId

        this.overrides.push(_.extend(options, _.cloneDeep(overrideInitialValues)))
        this.editingId = options.id
      }
    },
    handleDragSelection (startEnd) {
      if (startEnd.length !== 2 || startEnd[0].length < 2 || startEnd[1].length < 2) return

      const options = {
        rowStart: _.min([this.getRowName(startEnd[0]), this.getRowName(startEnd[1])]),
        rowEnd: _.max([this.getRowName(startEnd[0]), this.getRowName(startEnd[1])]),
        columnStart: _.min([this.getColumn(startEnd[0]), this.getColumn(startEnd[1])]),
        columnEnd: _.max([this.getColumn(startEnd[0]), this.getColumn(startEnd[1])])
      }

      // Prevent creating duplicate overrides for identical selections
      if (_.some(this.overrides, options)) return

      options.id = this.nextOverrideId

      this.overrides.push(_.extend(options, _.cloneDeep(overrideInitialValues)))
      this.editingId = options.id
    },
    deleteOverride (id) {
      this.config.overrides = this.overrides.filter(override => { return override.id !== id })
    },
    hasAnyOverridesSet (override) {
      return override.available !== null ||
        override.seatType !== null ||
        (override.seatName !== null && override.seatName.length > 0) ||
        override.customSeatType !== null ||
        override.limitTicketTypes !== null ||
        override.reserveDuringMarathons !== null ||
        (override.heldSeatSectionName !== null && override.heldSeatSectionName.length > 0) ||
        (override.confirmationRequired !== null && override.confirmationMessage !== null)
    },
    revert () {
      this.initializing = true
    }
  },
  models: {
    seatChart: {
      libraryDescendantSites: true,
      libraryChildSiteIds: true,
      watchQuery: false
    }
  }
}
</script>
<style lang="scss" scoped>
  .seat {
    flex-grow: 1;
    flex-shrink: 1;
    width: 20px;
    fill-opacity: 0;
  }
  .seat-content {
    width: 100%;
    position: relative;
  }
  svg {
    width: 100%;
    height: auto;
  }
  .seat-content {
    padding: 20% 5%;
  }
  .seat-content.regular-seat {
    padding: 20% 10%;
  }

  .seat-content.couch-left, .seat-content.couch-corner-left {
    padding: 20% 0% 20% 10%;
  }

  .seat-content.couch-vertical-east-left {
    padding: 40% 10% 0% 10%;
  }

  .seat-content.couch-right, .seat-content.couch-corner-right {
    padding: 20% 10% 20% 0%;
  }

  .seat-content.couch-vertical-west-right {
    padding: 40% 10% 0% 10%;
  }

  .seat-content.couch-center {
    padding: 20% 0%;
  }

  .seat-content.couch-vertical-east-center {
    margin-top: -10%;
    margin-bottom: -20%;
    padding: 0%;
  }

  .seat-content.couch-vertical-west-center {
    margin-top: -10%;
    margin-bottom: -20%;
    padding: 0%;
  }

  .seat-content.table, .seat-content.table-left, .seat-content.table-right, .seat-content.table-center {
    padding: 30% 0 0 0;
  }

  .seat-content.table-left {
    padding: 30% 0 0 0;
  }

  .seat-content.table-right {
    padding: 30% 0 0 0;
  }

  .seat-content.table-center {
    padding: 30% 0 0 0;
  }

  .show-scrollbars {
    overflow: auto;
  }

  .social-distancing {
    opacity: 0.4
  }

</style>
